import React from "react";
import { useTranslation } from "react-i18next";
import {
  createDriverSupportThread,
  getDriverSupportThread,
  listDriverSupportThreads,
  sendDriverSupportMessage,
} from "../api/support.js";

// REISOR_STAGE22P2M_DRIVER_DISPATCHER_SUPPORT_CHAT_TG_NOTIFY_V1
// Settings-only compact support chat. Do not confuse with order/client chat.
// REISOR_SUPPORT_CHAT_COMPACT_UI_V3
// REISOR_SUPPORT_CHAT_COMPACT_UI_V31
export default function DriverSupportChat({ driverToken }) {
  const { t } = useTranslation();
  const [threads, setThreads] = React.useState([]);
  const [activeId, setActiveId] = React.useState("");
  const [messages, setMessages] = React.useState([]);
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const scrollerRef = React.useRef(null);

  const activeThread = React.useMemo(
    () => threads.find((x) => String(x.id) === String(activeId)) || null,
    [threads, activeId]
  );

  const loadThreads = React.useCallback(async () => {
    const data = await listDriverSupportThreads(driverToken);
    const list = Array.isArray(data?.threads) ? data.threads : [];
    setThreads(list);
    if (!activeId && list[0]?.id) setActiveId(list[0].id);
    return list;
  }, [driverToken, activeId]);

  const loadThread = React.useCallback(async (id) => {
    if (!id) return;
    const data = await getDriverSupportThread(id, driverToken);
    if (data?.thread) {
      setThreads((prev) => {
        const next = prev.filter((x) => String(x.id) !== String(data.thread.id));
        return [data.thread, ...next].sort((a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0));
      });
    }
    setMessages(Array.isArray(data?.messages) ? data.messages : []);
  }, [driverToken]);

  React.useEffect(() => {
    let alive = true;
    setErr("");
    loadThreads()
      .then((list) => {
        if (!alive) return;
        const id = activeId || list?.[0]?.id || "";
        if (id) loadThread(id).catch(() => {});
      })
      .catch((e) => alive && setErr(e?.message || String(e)));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    loadThread(activeId).catch((e) => setErr(e?.message || String(e)));
  }, [activeId, loadThread]);

  React.useEffect(() => {
    try {
      const el = scrollerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {}
  }, [messages.length, activeId]);

  const send = async () => {
    const body = String(text || "").trim();
    if (!body || busy) return;

    setBusy(true);
    setErr("");
    try {
      let data;
      if (!activeId) {
        data = await createDriverSupportThread({ text: body }, driverToken);
        if (data?.thread?.id) setActiveId(data.thread.id);
      } else {
        data = await sendDriverSupportMessage(activeId, body, driverToken);
      }
      setText("");
      await loadThreads();
      const id = data?.thread?.id || activeId;
      if (id) await loadThread(id);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const statusText = (s) => {
    const x = String(s || "");
    if (x === "resolved") return t("support.status_resolved");
    if (x === "closed") return t("support.status_closed");
    if (x === "rejected") return t("support.status_rejected");
    if (x === "in_progress") return t("support.status_in_progress");
    return t("support.status_open");
  };

  return (
    <div className="reisor-driver-support-chat flex min-h-0 flex-1 flex-col gap-1">
      {threads.length > 1 ? (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {threads.slice(0, 12).map((thread) => (
            <button
              key={thread.id}
              type="button"
              onClick={() => setActiveId(thread.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-left text-xs ${
                activeId === thread.id
                  ? "bg-emerald-500/15 text-emerald-100"
                  : "bg-slate-800/30 text-slate-200"
              }`}
            >
              <div className="font-bold">{statusText(thread.status)}</div>
              <div className="max-w-[180px] truncate opacity-80">{thread.lastMessageText || t("support.thread")}</div>
            </button>
          ))}
        </div>
      ) : null}

      <div
        ref={scrollerRef}
        className="min-h-[300px] max-h-[66vh] flex-1 overflow-y-auto px-0.5 py-0.5"
      >
        {!messages.length ? (
          <div className="flex h-[220px] items-center justify-center text-center text-sm leading-6 text-slate-300/75">
            {t("support.empty")}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {messages.map((m) => {
              const mine = m.senderRole === "driver";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`text-sm leading-5 ${mine ? "max-w-[88%] rounded-[14px] px-2.5 py-1.5" : "max-w-[98%] px-0.5 py-0.5"} ${
                      mine
                        ? "bg-yellow-400 text-slate-950"
                        : "bg-transparent text-slate-50"
                    }`}
                  >
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    <div className={`mt-1 text-[10px] ${mine ? "text-slate-700" : "text-slate-400"}`}>
                      {mine ? t("support.you") : m.senderRole === "system" ? "REISOR" : t("support.dispetcher")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {activeThread ? (
        <div className="text-xs text-slate-300/75">
          {t("support.thread_status")}: <span className="font-bold">{statusText(activeThread.status)}</span>
        </div>
      ) : null}

      {err ? (
        <div className="rounded-xl bg-red-500/10 p-2 text-xs text-red-100">
          {err}
        </div>
      ) : null}

      <div className="flex items-end gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("support.placeholder")}
          rows={1}
          maxLength={4000}
          className="min-h-[42px] max-h-[96px] flex-1 resize-none rounded-[14px] border border-slate-700/50 bg-slate-950/35 px-3 py-2.5 text-sm text-white outline-none placeholder:text-slate-500"
        />
        <button
          type="button"
          onClick={send}
          disabled={busy || !String(text || "").trim()}
          className="h-[42px] w-[48px] rounded-[14px] bg-yellow-400 px-2 text-lg font-black text-slate-950 disabled:opacity-50"
        >
          {busy ? "…" : "➤"}
        </button>
      </div>
    </div>
  );
}
